import { motion } from "framer-motion";
import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { routes } from '../../config/utility';
import { UserContext } from '../../context/UserContext';
import { toast } from 'react-toastify';
import ThemeButton from "../../components/atoms/ThemeButton";
import SecondaryThemeButton from "../../components/atoms/SecondaryThemeButton";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const ResetPassword = () => {
  const { contextLogin, updateContext } = useContext(UserContext);
  const { state } = useLocation();
  const [form, setForm] = useState({
    otp: '',
    password: '',
    password_confirmation: '',
  });
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      await api.post('/resend-otp', { email: state?.email });
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/reset-password', {
        email: state?.email,
        otp: form.otp,
        password: form.password,
        password_confirmation: form.password_confirmation,
      });
      contextLogin(response.data);
      updateContext();
      navigate(routes.dashboard);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(routes.login);
  };

  // f3efebc4

  return (
    <motion.div
      className="flex h-screen w-screen overflow-hidden bg-body-auth "
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full flex items-center justify-center relative backdrop-blur-lg">
        {/* Back Button */}
        <motion.button
          className="absolute top-8 left-8 z-20 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          onClick={handleBack}
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          <span className="text-sm font-medium">Back to Login</span>
        </motion.button>

        {/* Top Image Panel */}
        <motion.div
          className="absolute -top-8 left-30 max-w-lg z-10"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", duration: 0.8 }}
        >
          <img
            src="/assets/kids-header5.png"
            alt=""
            className="w-full h-full object-cover object-bottom"
          />
        </motion.div>

        {/* Reset Password Card */}
        <motion.div
          className="sm:h-[calc(100vh-30vh)] flex flex-col justify-center relative w-full max-w-3xl mx-auto bg-white/85 border border-white/20 rounded-2xl p-8 backdrop-blur-md shadow-xl sm:mt-56"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div
            className="text-center flex flex-col items-center justify-center mb-8"
            variants={itemVariants}
          >
            <motion.img
              src="/assets/FULLLOGO.svg"
              alt="Popout"
              className="w-64 mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            />
            <motion.h2
              className="text-2xl font-display font-bold text-dark"
              variants={itemVariants}
            >
              Reset Password
            </motion.h2>
            <motion.p
              className="text-grey mt-2"
              variants={itemVariants}
            >
              Enter the OTP sent to your email and choose a new password
            </motion.p>
          </motion.div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            {/* OTP Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm mb-2">OTP Code</label>
              <input
                type="text"
                name="otp"
                value={form.otp}
                onChange={handleChange}
                className=""
                placeholder="Enter OTP"
                required
              />
            </motion.div>

            {/* Password Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm mb-2">New Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className=""
                placeholder="Enter new password"
                required
              />
            </motion.div>

            {/* Confirm Password Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm mb-2">Confirm Password</label>
              <input
                type="password"
                name="password_confirmation"
                value={form.password_confirmation}
                onChange={handleChange}
                className=""
                placeholder="Confirm new password"
                required
              />
            </motion.div>

            {/* Actions */}
            <div className="space-y-6">
              {/* Primary Action */}
              <motion.div
                variants={itemVariants}
                className="flex justify-center items-center w-full"
              >
                <ThemeButton
                  type="submit"
                  label={loading ? "Resetting..." : "Reset Password"}
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="w-full max-w-xs"
                  primaryColor="bg-secondary"
                  secondaryColor="bg-accentSecondary"
                  textColor="#ffffff"
                  hoverTextColor="#160033"
                />
              </motion.div>

              {/* Secondary Action */}
              <motion.div
                variants={itemVariants}
                className="flex justify-center"
              >
                <SecondaryThemeButton
                  label={resendLoading ? "Sending..." : "Resend OTP"}
                  onClick={handleResendOtp}
                  disabled={resendLoading}
                  className="text-sm"
                />
              </motion.div>
            </div>
          </form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ResetPassword;
